/**
 * Main App component
 * Sets up routing and authentication
 */

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';

import { TeacherLayout } from '@/components/layout/TeacherLayout';
import { DashboardPage } from '@/pages/teacher/DashboardPage';
import { CreateSessionPage } from '@/pages/teacher/CreateSessionPage';
import { SessionsListPage } from '@/pages/teacher/SessionsListPage';
import { SessionDetailPage } from '@/pages/teacher/SessionDetailPage';
import { ClassDetailPage } from '@/pages/teacher/ClassDetailPage';
import { ResultsPage } from '@/pages/teacher/ResultsPage';
import { ProfilePage } from '@/pages/teacher/ProfilePage';
import { GameGuidePage } from '@/pages/teacher/GameGuidePage';
import { JoinPage } from '@/pages/student/JoinPage';
import { WaitingRoomPage } from '@/pages/student/WaitingRoomPage';
import { RoleViewPage } from '@/pages/student/RoleViewPage';
import HomePage from '@/pages/HomePage';

/**
 * Custom Amplify UI theme matching the app's design system
 */
const authTheme = {
  name: 'cesim-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: '#f4f6fb',
          20: '#e2e5ec',
          40: '#ced4de',
          60: '#12356B',
          80: '#12356B',
          90: '#0d2850',
          100: '#0d2850',
        },
      },
    },
    components: {
      authenticator: {
        router: {
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.12)',
          borderWidth: '0',
        },
      },
      button: {
        primary: {
          backgroundColor: '#367A25',
          _hover: {
            backgroundColor: '#2d6820',
          },
          _active: {
            backgroundColor: '#2d6820',
          },
        },
      },
      tabs: {
        item: {
          _active: {
            borderColor: '#12356B',
            color: '#12356B',
          },
          _hover: {
            color: '#12356B',
          },
        },
      },
      fieldcontrol: {
        _focus: {
          borderColor: '#12356B',
          boxShadow: '0 0 0 2px rgba(18, 53, 107, 0.2)',
        },
      },
    },
    radii: {
      small: '6px',
      medium: '8px',
      large: '12px',
    },
  },
};

/**
 * Custom header component for the Authenticator
 */
function AuthHeader() {
  return (
    <div className="text-center p-6">
      <img
        alt="Centauri Logo"
        src="/icons/centauri-logo.png"
        className="h-20 mx-auto object-contain"
      />
      
      <h3 className="text-sm text-slate-600 mt-1">
        Sign in to manage your simulation sessions
      </h3>
    </div>
  );
}

/**
 * Custom footer component for the Authenticator
 */
function AuthFooter() {
  return (
    <div className="text-center p-4">
      <p className="text-xs text-slate-500">
        &copy; {new Date().getFullYear()} Centauri Simulation Platform
      </p>
    </div>
  );
}

/**
 * Authenticator components configuration
 */
const authComponents = {
  Header: AuthHeader,
  Footer: AuthFooter,
};

/**
 * Protected routes wrapper - requires authentication
 */
function TeacherRoutes() {
  return (
    <ThemeProvider theme={authTheme}>
      <Authenticator signUpAttributes={['email']} components={authComponents}>
        {({ signOut, user }) => (
          <Routes>
            <Route
              path="teacher"
              element={<TeacherLayout signOut={signOut} user={user} />}
            >
              <Route index element={<DashboardPage />} />
              <Route path="new" element={<CreateSessionPage />} />
              <Route path="sessions" element={<SessionsListPage />} />
              <Route path="sessions/:sessionId" element={<SessionDetailPage />} />
              <Route path="sessions/:sessionId/results" element={<ResultsPage />} />
              <Route path="classes/:classId" element={<ClassDetailPage />} />
              <Route path="guide" element={<GameGuidePage />} />
              <Route path="profile" element={<ProfilePage user={user} />} />
            </Route>
            <Route path="/" element={<Navigate to="/login/teacher" replace />} />
          </Routes>
        )}
      </Authenticator>
    </ThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<Navigate to="/" replace />} />

        {/* Student routes - NO authentication required */}
        <Route path="/join" element={<JoinPage />} />
        <Route path="/student/waiting/:sessionId" element={<WaitingRoomPage />} />
        <Route path="/student/role/:sessionId" element={<RoleViewPage />} />

        {/* Teacher routes - authentication required */}
        <Route path="/login/*" element={<TeacherRoutes />} />

        {/* Catch-all: unknown routes go to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
